// Prevent the native "ResizeObserver loop completed with undelivered notifications"
// / "ResizeObserver loop limit exceeded" browser errors from firing at all.
// antd's Table/Modal/Form create ResizeObservers internally (via rc-resize-observer);
// deferring their callback to the next animation frame avoids the synchronous notify
// loop the browser flags.
//
// This must be the FIRST import in index.js (before React/App). ES module imports
// always evaluate before the rest of a file's top-level code, regardless of where
// they're textually placed relative to other statements in that file - so if this
// patch were placed after `import App from './App'`, antd's rc-resize-observer would
// already have captured a reference to the original native ResizeObserver by the time
// this code ran, making the patch a no-op.
//
// Suppressing the resulting window 'error' event afterwards does not work as an
// alternative: in dev, react-error-overlay registers its own 'error' listener in an
// earlier webpack entry (react-dev-utils/webpackHotDevClient) than this app's code, so
// it always sees the event first and shows its overlay before any listener here could
// suppress it.
//
// Deferring by a frame introduces a race for consumers (e.g. ace-builds' VirtualRenderer)
// that call disconnect() synchronously on teardown and then null out the state their
// callback relies on in that same tick - the deferred callback can still fire a frame
// later against already-nulled state. Track disconnection per-instance and skip the
// deferred callback once disconnect() has been called to avoid that use-after-teardown crash.
if (typeof window !== 'undefined' && window.ResizeObserver) {
    const OriginalResizeObserver = window.ResizeObserver;
    window.ResizeObserver = class extends OriginalResizeObserver {
        constructor(callback) {
            let disconnected = false;
            super((entries, observer) => {
                window.requestAnimationFrame(() => {
                    if (!disconnected) callback(entries, observer);
                });
            });
            this.$disconnected = () => { disconnected = true; };
        }

        disconnect() {
            this.$disconnected();
            super.disconnect();
        }
    };
}
